/**
 * Dynamic Input store (2D-style cursor-following command entry).
 *
 * Carries only transient UI state for the dynamic-input tooltip: the typed
 * buffer, live autocomplete, the cursor coordinate readout and a short-lived
 * feedback line. Geometry (world points, camera binding) lives in
 * core/dynamicInput/dynamicInputEngine, not here.
 */

import { create } from 'zustand';
import { useAppStore } from './store';
import { useDynBoxStore, isDynBoxEntryKey } from './useDynBoxStore';

export type DynamicFeedbackTone = 'ok' | 'error';

export interface DynamicFeedback {
    text: string;
    tone: DynamicFeedbackTone;
    /** Monotonic id so the provider can schedule its own clear. */
    seq: number;
}

interface DynamicInputState {
    /** Text typed at the cursor (empty = idle). */
    buffer: string;
    /** Autocomplete candidates for the buffer. */
    suggestions: string[];
    /** Cursor-following coordinate readout, e.g. "5.00, 3.00   4.2 < 31". */
    readout: string | null;
    /** Transient commit/error line shown under the buffer. */
    feedback: DynamicFeedback | null;
    /** Shared command-log lines (bottom command line + cursor tooltip). */
    commandLog: string[];
    /** Executed inputs for ArrowUp/ArrowDown recall (memory only). */
    inputHistory: string[];
    /**
     * Recently executed commands (Faz 4.7 — OCS recent_commands parity):
     * every command SOURCE lands here (typed line, ribbon button, chip,
     * LISP command:) — the Repeat right-click menu reads it. Duplicates are
     * skipped, most recent first, capped at 50. Persisted per session in
     * memory only (OCS keeps it in the session too).
     */
    recentCommands: string[];
    /** True while a CAD workbench has mounted its dynamic-input provider. */
    captureActive: boolean;

    setBuffer: (value: string) => void;
    setSuggestions: (value: string[]) => void;
    setReadout: (value: string | null) => void;
    setFeedback: (text: string, tone: DynamicFeedbackTone) => void;
    clearFeedback: (seq: number) => void;
    appendLog: (lines: string[]) => void;
    pushHistory: (value: string) => void;
    pushRecentCommand: (name: string) => void;
    setCaptureActive: (value: boolean) => void;
    reset: () => void;
}

const INITIAL_LOG = [
    'Yap - Dynamic Input',
    'Type a command near the cursor, Space/Enter to run',
    '',
];

let feedbackSeq = 0;

export const useDynamicInputStore = create<DynamicInputState>()((set, get) => ({
    buffer: '',
    suggestions: [],
    readout: null,
    feedback: null,
    commandLog: INITIAL_LOG,
    inputHistory: [],
    recentCommands: [],
    captureActive: false,

    setBuffer: (value) => set({ buffer: value }),
    setSuggestions: (value) => set({ suggestions: value }),
    // Content guard: the engine writes this on every pointermove while a
    // command is active; dropping identical strings stops the per-move React
    // re-render of the tooltip provider.
    setReadout: (value) => {
        if (get().readout === value) return;
        set({ readout: value });
    },
    setFeedback: (text, tone) => set({ feedback: { text, tone, seq: ++feedbackSeq } }),
    clearFeedback: (seq) => {
        if (get().feedback?.seq === seq) set({ feedback: null });
    },
    appendLog: (lines) => set((state) => ({
        // Cap the transcript so a long drafting session (F2 window, wheel
        // scrolling) never grows the array unboundedly.
        commandLog: [...state.commandLog, ...lines].slice(-2000),
    })),
    pushHistory: (value) => set((state) => ({
        inputHistory: [...state.inputHistory.slice(-49), value],
    })),
    // OCS recent_commands semantics: skip consecutive duplicates, most
    // recent first, cap 50.
    pushRecentCommand: (name) => set((state) => {
        const clean = name.trim();
        if (!clean) return state;
        if (state.recentCommands[0]?.toUpperCase() === clean.toUpperCase()) return state;
        return {
            recentCommands: [clean, ...state.recentCommands.filter(
                (c) => c.toUpperCase() !== clean.toUpperCase()
            )].slice(0, 50),
        };
    }),
    setCaptureActive: (value) => set({ captureActive: value }),
    reset: () => set({ buffer: '', suggestions: [], feedback: null }),
}));

/**
 * Single source of truth for "is dynamic input in charge of this keystroke?".
 * The provider (document capture) and the legacy KeyboardListener (window
 * bubble) must answer identically, or the same key gets consumed twice.
 */
export function isDynamicInputActive(): boolean {
    const { captureActive } = useDynamicInputStore.getState();
    if (!captureActive) return false;
    return useAppStore.getState().interactionOptions.dynamicInput;
}

const FUNCTION_KEY = /^F\d{1,2}$/;

/**
 * Keys the dynamic provider owns while active. Everything else (F-keys,
 * Delete, modifier combos) falls through to the global shortcut system.
 * Both the provider (capture) and KeyboardListener (bubble) must ask with
 * the same event so a keystroke is consumed exactly once.
 */
export function dynamicOwnsKeyEvent(e: KeyboardEvent): boolean {
    if (!isDynamicInputActive()) return false;
    if (e.ctrlKey || e.altKey || e.metaKey) return false;
    if (FUNCTION_KEY.test(e.key)) return false;
    // DynBox field entry (Faz 11.3): while the balloon shows editable fields
    // the keyboard walks them — Tab/Enter route into the field handler even
    // with an empty command buffer (fields are typed without the buffer).
    const box = useDynBoxStore.getState();
    if (box.mode !== null) {
        if (e.key === 'Tab' || e.key === 'Enter') return true;
        if (box.activeIndex !== null
            && (e.key === 'Backspace' || e.key === 'Escape' || isDynBoxEntryKey(e.key))) {
            return true;
        }
        if (box.activeIndex === null && isDynBoxEntryKey(e.key)) return true;
    }
    // With an empty buffer only non-space printable characters engage
    // Dynamic Input; Space (with nothing typed) confirms the active tool
    // instead, and Enter/Esc/Tab/arrows/Backspace stay with the tool and
    // the legacy shortcut pipeline (classic confirm/repeat behavior).
    const hasBuffer = useDynamicInputStore.getState().buffer.length > 0;
    if (!hasBuffer) {
        return e.key.length === 1 && e.key > ' ';
    }
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ' ') return true;
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') return true;
    if (e.key === 'Backspace' || e.key === 'Escape') return true;
    // Printable characters (letters, digits, '@', ',', '.', '<', '-', …)
    return e.key.length === 1 && e.key >= ' ';
}
